import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PRODUCT_NAME, PRODUCT_TAGLINE, ARC_LOGO_PATH } from '../brand';

const LOGOUT_MESSAGES = {
  idle: 'You were signed out after 30 minutes without activity. Sign in to pick up where you left off.',
  expired: 'Your session has ended. Sign in to pick up where you left off.',
  step_up: 'Please re-authenticate to continue.',
  // Signing out ends a person's sessions everywhere, so their other windows land here too.
  revoked: 'You were signed out. If you just signed out in another window, sign in again below. If you can’t sign in, your access may have been removed — ask your organization’s admin.',
};

// Google sign-in callback errors (google_identity provider). Codes are
// deliberately coarse — the backend never says whether an account exists.
// `mfa_required` gets its own guided panel below instead of a one-liner.
const SIGNIN_ERRORS = {
  google_denied: 'Sign-in was cancelled.',
  state: 'That sign-in attempt expired. Please try again.',
  nonce: 'That sign-in attempt expired. Please try again.',
  exchange: 'We couldn’t complete sign-in with Google. Please try again.',
  verify: 'We couldn’t verify your Google sign-in. Please try again.',
  domain: 'That was a personal Google account. Sign in with your ChargeStack login ID instead — it looks like name@app.chargestack.ai and is on your invite.',
  missing_code: 'Sign-in didn’t complete. Please try again.',
  mfa_check: 'We couldn’t confirm your 2-Step Verification status just now. Please try again in a minute.',
};

// Google's 2-Step enrolment page, opened on a specific account.
const ENROLL_2SV_URL = 'https://myaccount.google.com/signinoptions/two-step-verification/enroll';
function enrollUrl(account) {
  if (!account) return ENROLL_2SV_URL;
  return `https://accounts.google.com/AccountChooser?Email=${encodeURIComponent(account)}&continue=${encodeURIComponent(ENROLL_2SV_URL)}`;
}
function recoveryUrl(account) {
  const base = 'https://accounts.google.com/signin/recovery';
  return account ? `${base}?Email=${encodeURIComponent(account)}` : base;
}

// Carousel breaker: if an auto-redirect to the Portal bounced us straight
// back here (Portal hands a token, the session is dead/locked server-side,
// the app ejects), do NOT auto-redirect again within this window. Every lap
// of that loop fires a burst of authenticated calls and re-trips the per-IP
// auth lockout — the exact zombie-tab self-DoS the lockout exists to stop.
// sessionStorage is per-tab and survives the cross-origin round-trip.
const AUTO_REDIRECT_COOLDOWN_MS = 60_000;
const AUTO_REDIRECT_STAMP_KEY = 'dc_last_auto_portal_redirect';

// Two providers, one page:
//  - firebase: customers sign in via the ChargeStack Portal (chargestack.ai),
//    which bounces back here with a token. This page is a brief transit
//    screen that redirects to the Portal (plus the dev-mode button).
//  - google_identity: email-first form → backend-brokered Google sign-in
//    with login_hint pre-selecting the hosted account (plan P1.6). A known
//    login ID (from the onboarding link or the last sign-in) becomes a
//    one-click "Continue as …"; the form only asks when we don't know.
//    The person types their WORK email; the backend maps it to the hosted
//    login ID (name@app.chargestack.ai) so nobody has to remember it.
export default function Login() {
  const { user, loading, login, devMode, signInViaPortal, authProvider, signinDomain, startGoogleSignIn } = useAuth();
  const navigate = useNavigate();
  // Read synchronously at first render: the auto-redirect effect below runs
  // in the same commit as the effect that copies this into state, so it would
  // otherwise fire with logoutReason still null and drop `reauth`.
  const initialReasonRef = useRef(sessionStorage.getItem('dc_logout_reason'));
  const [logoutReason, setLogoutReason] = useState(initialReasonRef.current);
  const [signinError, setSigninError] = useState(null);
  const [mfaAccount, setMfaAccount] = useState(null);   // set when error=mfa_required
  const [knownAccount, setKnownAccount] = useState(''); // hosted login ID we already know
  const [useOther, setUseOther] = useState(false);
  const [email, setEmail] = useState('');
  const redirectedRef = useRef(false);
  const isGoogle = authProvider === 'google_identity';
  const domain = (signinDomain || 'app.chargestack.ai').toLowerCase();

  // After an idle/expired signout, the IdP must NOT silently resume its
  // cached session (that skips the MFA challenge entirely) — reauth forces a
  // fresh password+MFA login, then bounces straight back here.
  const needsReauth = logoutReason === 'idle' || logoutReason === 'expired';

  useEffect(() => {
    if (user) navigate('/cases', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const reason = sessionStorage.getItem('dc_logout_reason');
    if (reason) {
      setLogoutReason(reason);
      sessionStorage.removeItem('dc_logout_reason');
    }
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const paramHint = params.get('login_hint') || params.get('email') || '';
    const stored = localStorage.getItem('dc_login_hint') || '';
    if (err === 'mfa_required') {
      setMfaAccount(paramHint || stored || '');
    } else if (err) {
      setSigninError(SIGNIN_ERRORS[err] || 'Sign-in didn’t complete. Please try again.');
    }
    // Onboarding hands us the hosted login ID; otherwise the last sign-in's.
    const hint = paramHint || stored;
    if (hint) {
      setEmail(hint);
      if (hint.toLowerCase().endsWith('@' + domain)) setKnownAccount(hint);
    }
    if (err || paramHint) {
      window.history.replaceState({}, '', '/login');
    }
  }, [domain]);

  // Auto-redirect in production. Dev mode keeps the local button. Under
  // google_identity only auto-redirect when we already know which account
  // to route to (a stored hint) and nothing just failed — otherwise show the
  // email-first form.
  const [autoRedirectSuppressed, setAutoRedirectSuppressed] = useState(false);
  useEffect(() => {
    if (loading || devMode || user || redirectedRef.current) return;
    // Access removed: show the message and stay put — no silent bounce back
    // into Google sign-in (a re-invite is the only way back in).
    if (logoutReason === 'revoked') return;
    if (isGoogle) {
      const params = new URLSearchParams(window.location.search);
      const storedHint = localStorage.getItem('dc_login_hint');
      if (!storedHint || params.get('error') || params.get('login_hint')) return;
    }
    const last = Number(sessionStorage.getItem(AUTO_REDIRECT_STAMP_KEY) || 0);
    if (Date.now() - last < AUTO_REDIRECT_COOLDOWN_MS) {
      // We auto-bounced recently and we're back at /login — the handoff is
      // not producing a working session. Go quiescent; the button remains
      // for a deliberate retry (which restarts the cooldown).
      setAutoRedirectSuppressed(true);
      return;
    }
    redirectedRef.current = true;
    // Tiny delay so the logout reason is visible briefly before the bounce.
    const t = setTimeout(() => {
      sessionStorage.setItem(AUTO_REDIRECT_STAMP_KEY, String(Date.now()));
      signInViaPortal({ reauth: needsReauth });
    }, logoutReason ? 1200 : 0);
    return () => clearTimeout(t);
  }, [loading, devMode, user, logoutReason, needsReauth, signInViaPortal, isGoogle]);

  if (loading) {
    return <div className="loading-screen">Loading…</div>;
  }

  const reauth = needsReauth || logoutReason === 'step_up';

  function go(hint, opts = {}) {
    sessionStorage.setItem(AUTO_REDIRECT_STAMP_KEY, String(Date.now()));
    startGoogleSignIn(hint, { reauth, ...opts });
  }

  function submitGoogle(e) {
    e.preventDefault();
    const hint = email.trim();
    if (!hint) return;
    go(hint);
  }

  function useDifferentAccount() {
    localStorage.removeItem('dc_login_hint');
    setKnownAccount('');
    setEmail('');
    setUseOther(true);
  }

  const typedIsLoginId = email.trim().toLowerCase().endsWith('@' + domain);
  const accountForLinks = knownAccount || (typedIsLoginId ? email.trim() : '');
  // --muted in app.css is a BACKGROUND tint (#f1f3f5); text needs --text-muted (2026-10-09:
  // the sign-in labels and help text were near-white on white).
  const muted = { color: 'var(--text-muted, #6c757d)' };

  function renderGoogle() {
    if (mfaAccount !== null) {
      return (
        <div className="login-form">
          <h3 style={{ margin: '12px 0 4px' }}>One more step: 2-Step Verification</h3>
          <p style={{ ...muted, margin: '0 0 12px' }}>
            Your password is set. ChargeStack also requires 2-Step Verification on
            {mfaAccount ? <> <strong>{mfaAccount}</strong></> : ' your ChargeStack login ID'}.
            A passkey (Touch ID / Windows Hello) is quickest; an authenticator app also works.
          </p>
          <a className="btn btn-primary btn-lg" href={enrollUrl(mfaAccount)} target="_blank" rel="noopener noreferrer">
            Set up 2-Step Verification on Google
          </a>
          <button type="button" className="btn btn-secondary btn-lg" style={{ marginTop: 10 }}
            onClick={() => go(mfaAccount || email.trim(), { reauth: true })}>
            I’ve set it up — sign in again
          </button>
          <p style={{ ...muted, fontSize: 12, marginTop: 12 }}>
            Google opens in a new tab. Use the same login ID there, not a personal Google account.
          </p>
        </div>
      );
    }

    if (knownAccount && !useOther) {
      return (
        <div className="login-form">
          <p style={{ ...muted, margin: '12px 0 8px' }}>
            {autoRedirectSuppressed
              ? 'Sign-in didn’t complete. Click below to try again.'
              : 'Signing you in through Google as'}
          </p>
          <div className="mono" style={{ fontSize: 14, marginBottom: 12 }}>{knownAccount}</div>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => go(knownAccount)}>
            Continue as {knownAccount.split('@')[0]}
          </button>
          <p style={{ ...muted, fontSize: 12, marginTop: 12 }}>
            <a href="#" onClick={e => { e.preventDefault(); useDifferentAccount(); }}>Not you? Use a different account</a>
            {' · '}
            <a href={recoveryUrl(knownAccount)} target="_blank" rel="noopener noreferrer">Forgot your password?</a>
          </p>
        </div>
      );
    }

    return (
      <form onSubmit={submitGoogle} className="login-form">
        <label className="form-label" htmlFor="login-email" style={{ color: 'var(--color-text, #1f2937)', fontWeight: 600, fontSize: 15, display: 'block', margin: '16px 0 8px', textAlign: 'left' }}>
          Work email or ChargeStack login ID
        </label>
        <input
          id="login-email"
          className="form-input login-email-input"
          type="email"
          autoComplete="username"
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          autoFocus
        />
        <p style={{ ...muted, fontSize: 14, lineHeight: 1.5, margin: '10px 0 0', textAlign: 'left' }}>
          {typedIsLoginId
            ? 'That’s your ChargeStack login ID. You’ll finish on Google’s sign-in page.'
            : <>You’ll finish on Google’s sign-in page, signed in with your ChargeStack login ID
                {' '}(it looks like <span className="mono">name@{domain}</span>), not a personal Google account.</>}
        </p>
        <button type="submit" className="btn btn-primary btn-lg" style={{ marginTop: 16, width: '100%' }}>
          Continue
        </button>
        <p style={{ ...muted, fontSize: 14, lineHeight: 1.5, marginTop: 14 }}>
          <a href={recoveryUrl(accountForLinks)} target="_blank" rel="noopener noreferrer">Forgot your password?</a>
          {' '}Reset it with Google using your login ID — the code goes to your work email.
        </p>
      </form>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src={ARC_LOGO_PATH} alt={PRODUCT_NAME} className="login-logo-img" />
        </div>
        <div className="login-tagline">{PRODUCT_TAGLINE}</div>
        {logoutReason && (
          <div className="login-notice">
            {LOGOUT_MESSAGES[logoutReason] || 'You have been signed out.'}
          </div>
        )}
        {signinError && <div className="login-notice error-text">{signinError}</div>}
        {devMode ? (
          <button className="btn btn-primary btn-lg" onClick={async () => {
            try { await login(); } catch (e) { console.error('Login failed:', e); }
          }}>
            Enter DEV MODE
          </button>
        ) : isGoogle ? renderGoogle() : (
          <>
            <p style={{ ...muted, margin: '12px 0 16px' }}>
              {autoRedirectSuppressed
                ? 'Sign-in didn’t complete. Click below to try again.'
                : 'Redirecting to ChargeStack to sign in…'}
            </p>
            <button className="btn btn-primary btn-lg" onClick={() => {
              sessionStorage.setItem(AUTO_REDIRECT_STAMP_KEY, String(Date.now()));
              signInViaPortal({ reauth: needsReauth });
            }}>
              Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
